<template lang="pug">
.users
  //- Barra de navegación
  van-nav-bar(:title="$t('users.title')")
    van-icon(name="menu" slot="left" @click="openSidenav")
    van-icon(name="retry" slot="right" @click="getUsers()")

  .main
    //- Listado de instalaciones
    .units__installations(
      v-for="(installation, installationKey) in installations"
      :key="installationKey")

      .units__installations__bar
        .units__installations__name {{ installation.name }}
        .units__installations__action
          van-icon(name="more" @click="showManageUsers(installationKey)")

      van-cell-group
        //- Listado de usuarios por instalación
        van-cell(
          v-for="(user, key) in installation.users"
          icon="user"
          :key="key"
          :title="user.name + ' ' + user.lastName"
          :value="typeUser(user.type)"
          :label="user.email"
          @click="showManageUsers(installationKey, user._id)"
          is-link)

      van-cell-group(v-if="showUsersPending")
        van-cell.delete(
          v-for="(user, key) in installation.pendingUsers"
          icon="user"
          :key="key"
          :title="user.email"
          :value="$t('schedule.delete')"
          :label="$t('users.pendingConfirmation')"
          @click="confirmDeletePendingUsers(installationKey, user._id)")

    //- Información sobre el tipo de usuarios
    .users__message
      .margin-halve-bottom {{ $t('users.advancedUser') }}

      router-link.link(to="/main/userFeatures")
        | {{ $t('users.userFeatures') }}
        van-icon(name="right")

  //- Modal para crear/editar permisos de usuario
  van-popup.van-popup--full(
    :value="openManageUsers"
    position="right"
    :overlay="false")

    ManageUsers(
      v-if="openManageUsers"
      :installationID="installationID"
      :user="user"
      @updateUser="updateUser"
      @deleteUser="deleteUser"
      @close="hideManageUsers")
</template>

<script>
import i18n from '@/locales/i18n';
import { getErrorId } from '@/services/error.service';
import UsersService from '@/services/users.service';
import ManageUsers from '@/components/Users/ManageUsers.vue';
import { CONFIG, USER_TYPE } from '@/app.config';
import { mapGetters, mapActions } from 'vuex';

export default {
  components: { ManageUsers },
  data() {
    return {
      installations: [],
      user: null,
      installationID: '',
      isLoading: false,
      usersError: null,
      openManageUsers: false,
      showUsersPending: CONFIG.USERS_PENDING,
    };
  },

  computed: {
    ...mapGetters({
      getAdvancedInstallations: 'unitsModule/getAdvancedInstallations',
    }),

    /**
     * Listado con todas las instalaciones
     *
     * @return {Array[Installation]} - Listado de instalaciones
     */
    installationsAdvanced() {
      return this.getAdvancedInstallations();
    },
  },

  watch: {
    installationsAdvanced() {
      this.getUsers();
    },
  },

  methods: {
    ...mapActions({
      openSidenav: 'globalModule/openSidenav',
    }),

    /**
     * Devuelve el tipo de usuario
     */
    typeUser(type) {
      return type === USER_TYPE.ADVANCED
        ? i18n.t('users.table.advanced')
        : i18n.t('users.table.basic');
    },

    /**
     * Obtiene un listado de instalaciones con sus respectivos usuarios
     */
    getUsers() {
      try {
        // Compruebo si hay una petición en curso,
        // si la hay, no hago nada
        if (this.isLoading || !Object.keys(this.installationsAdvanced).length) return;

        // Muestro el loader
        // Cuando muestro esta vista despues de añadir unidad no se quita el fondo del loading en el clear
        //
        // this.$toast.loading({
        //   duration: 0,
        //   mask: true,
        //   message: i18n.t('general.loading'),
        // });

        // Inicializo valores
        this.isLoading = true;
        this.usersError = null;
        this.installations = {};

        // Realizo la petición
        Object.keys(this.installationsAdvanced).forEach(async installationID => {
          const installationData = await UsersService.getUsers(installationID);
          this.$set(this.installations, installationID, installationData);
        });

        this.$toast.clear();
      } catch (error) {
        // Obtengo el error
        this.deviceError = getErrorId(error);

        this.$toast.clear();
        this.$toast.fail({
          mask: true,
          message: i18n.t('general.errors.default.message'),
        });
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Muestra el modal para editar un usuario
     */
    showManageUsers(installationID, userID) {
      this.installationID = installationID;

      if (userID) {
        this.user = this.installations[installationID].users[userID];
      }
      this.openManageUsers = true;
    },

    /**
     * Oculta el modal para editar un usario
     */
    hideManageUsers() {
      this.installationID = '';
      this.user = null;
      this.openManageUsers = false;
    },

    /**
     * Actualiza los datos de un usuario después de editarlo
     */
    updateUser(data) {
      const installationID = data.installationID;
      const userID = data.user.user_id;
      const type = data.user.type;
      const devices = data.user.permitted_devices;
      this.$set(this.installations[installationID].users[userID], 'type', type);
      this.$set(this.installations[installationID].users[userID], 'permitted_devices', devices);
      this.hideManageUsers();
    },

    deleteUser(data) {
      const installationID = data.installationID;
      const userID = data.user.user_id;
      this.$delete(this.installations[installationID].users, userID);
      this.hideManageUsers();
    },

    /**
     * Confirma si realmente quiere eliminar al usuario
     */
    confirmDeletePendingUsers(installationID, userID) {
      this.$dialog
        .confirm({
          title: i18n.t('users.deleteConfirm.title'),
          message: i18n.t('users.deleteConfirm.message'),
        })
        .then(() => {
          this.deletePendingUsers(installationID, userID);
        });
    },

    /**
     * Elimina a un usuario pendiente de confirmar
     */
    async deletePendingUsers(installationID, userID) {
      try {
        // Compruebo si hay una petición en curso,
        // si la hay, no hago nada
        if (this.isLoading) return;

        // Muestro el loader
        this.$toast.loading({
          duration: 0,
          mask: true,
          message: i18n.t('general.loading'),
        });

        // Inicializo valores
        this.isLoading = true;
        this.usersError = null;

        // Preparo los datos
        const user = { installationID, userID };

        await UsersService.deleteUserInvited(user);
        this.$toast.clear();

        this.$dialog
          .alert({
            title: i18n.t('users.delete.title'),
            message: i18n.t('users.delete.message'),
          })
          .then(() => {
            this.$delete(this.installations[installationID].pendingUsers, userID);
          });
      } catch (error) {
        // Obtengo el error
        this.usersError = getErrorId(error);

        this.$toast.clear();

        this.$dialog
          .alert({
            title: i18n.t('users.fails.title'),
            message: i18n.t('users.fails.message'),
          })
          .then(() => {
            this.$emit('close');
          });
      } finally {
        this.isLoading = false;
      }
    },
  },

  created() {
    this.getUsers();
  },
};
</script>
